// web/src/features/competition/wizard/hooks/useFakeProgress.ts
import { useEffect, useRef, useState } from 'react'

const START = 60
const CAP = 90
const STEP_MS = 400
const COMPLETE_HOLD_MS = 1000

export type UseFakeProgress = {
  progress: number
  active: boolean
  start: () => void
  complete: () => Promise<void>
  reset: () => void
}

/**
 * Drives a fake progress bar for non-deterministic async work (clone/create).
 * start() jumps to 60% and creeps toward a 90% cap; complete() snaps to 100%
 * and resolves after a 1s hold so the caller can show the full bar before
 * redirecting; reset() tears it all down.
 */
export const useFakeProgress = (): UseFakeProgress => {
  'use memo'
  const [progress, setProgress] = useState(0)
  const [active, setActive] = useState(false)
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null)
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  const clearCreep = () => {
    if (intervalRef.current !== null) {
      clearInterval(intervalRef.current)
      intervalRef.current = null
    }
    if (timeoutRef.current !== null) {
      clearTimeout(timeoutRef.current)
      timeoutRef.current = null
    }
  }

  const start = () => {
    clearCreep()
    setActive(true)
    setProgress(START)
    intervalRef.current = setInterval(() => {
      // Ease toward CAP: each tick closes a fraction of the remaining gap, so
      // it slows as it approaches and never reaches the cap.
      setProgress((prev) => {
        if (prev >= CAP) {
          return prev
        }
        const next = prev + (CAP - prev) * 0.05
        return next >= CAP - 0.5 ? CAP : next
      })
    }, STEP_MS)
  }

  const complete = (): Promise<void> => {
    clearCreep()
    setProgress(100)
    return new Promise<void>((resolve) => {
      timeoutRef.current = setTimeout(() => {
        timeoutRef.current = null
        resolve()
      }, COMPLETE_HOLD_MS)
    })
  }

  const reset = () => {
    clearCreep()
    setActive(false)
    setProgress(0)
  }

  useEffect(() => () => clearCreep(), [])

  return { progress, active, start, complete, reset }
}
